/**
 * 导出文件校验 + 版本迁移。无第三方依赖，手写校验（前后端共用）。
 * 校验失败整体拒绝，不做半截导入。
 */
import {
  EXPORT_VERSION,
  IMPORT_LIMITS,
  type DayNoteExport,
  type ExportGroup,
  type ExportDayNote,
  type ExportItem,
} from './types';

export type ValidateResult =
  | { ok: true; data: DayNoteExport }
  | { ok: false; error: string };

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const COLOR_RE = /^#[0-9A-Fa-f]{6}$/;

function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function validateGroup(g: unknown, i: number): string | null {
  if (!isObj(g)) return `groups[${i}] 不是对象`;
  if (typeof g.abbr !== 'string' || !g.abbr.trim()) return `groups[${i}].abbr 非法`;
  if (g.abbr.length > 10) return `groups[${i}].abbr 过长`;
  if (typeof g.name !== 'string' || !g.name.trim()) return `groups[${i}].name 非法`;
  if (g.name.length > 50) return `groups[${i}].name 过长`;
  if (g.color != null && (typeof g.color !== 'string' || !COLOR_RE.test(g.color)))
    return `groups[${i}].color 非法`;
  if (g.sortOrder != null && typeof g.sortOrder !== 'number')
    return `groups[${i}].sortOrder 非法`;
  return null;
}

function validateItem(it: unknown, di: number, ii: number): string | null {
  const at = `dayNotes[${di}].items[${ii}]`;
  if (!isObj(it)) return `${at} 不是对象`;
  if (typeof it.content !== 'string' || !it.content.trim()) return `${at}.content 非法`;
  if (it.content.length > 2000) return `${at}.content 过长`;
  if (it.note != null && typeof it.note !== 'string') return `${at}.note 非法`;
  if (it.color != null && (typeof it.color !== 'string' || !COLOR_RE.test(it.color)))
    return `${at}.color 非法`;
  if (it.hours != null && (typeof it.hours !== 'number' || it.hours < 0 || it.hours > 24))
    return `${at}.hours 必须在 0–24`;
  if (it.isDone != null && typeof it.isDone !== 'boolean') return `${at}.isDone 非法`;
  if (it.groupAbbr != null && typeof it.groupAbbr !== 'string') return `${at}.groupAbbr 非法`;
  return null;
}

/** 把任意来源对象升级到当前版本（迁移链入口；目前只有 v1） */
function migrate(raw: Record<string, unknown>): Record<string, unknown> {
  // 未来版本在此追加：if (version < 2) { ...升级到 v2... }
  return raw;
}

export function validateExport(raw: unknown): ValidateResult {
  if (!isObj(raw)) return { ok: false, error: '文件内容不是合法对象' };
  if (raw.app !== 'daynote') return { ok: false, error: '不是 DayNote 导出文件' };
  if (typeof raw.version !== 'number' || !Number.isInteger(raw.version))
    return { ok: false, error: 'version 字段缺失或非法' };
  if (raw.version > EXPORT_VERSION)
    return { ok: false, error: '文件由更新版本导出，请升级应用后再导入' };

  const migrated = migrate(raw);
  const data = migrated.data;
  if (!isObj(data)) return { ok: false, error: 'data 字段非法' };

  const groups = data.groups;
  const dayNotes = data.dayNotes;
  if (!Array.isArray(groups)) return { ok: false, error: 'data.groups 必须是数组' };
  if (!Array.isArray(dayNotes)) return { ok: false, error: 'data.dayNotes 必须是数组' };

  if (groups.length > IMPORT_LIMITS.maxGroups)
    return { ok: false, error: `分组数超过上限 ${IMPORT_LIMITS.maxGroups}` };
  if (dayNotes.length > IMPORT_LIMITS.maxDayNotes)
    return { ok: false, error: `日清单数超过上限 ${IMPORT_LIMITS.maxDayNotes}` };

  for (let i = 0; i < groups.length; i++) {
    const err = validateGroup(groups[i], i);
    if (err) return { ok: false, error: err };
  }

  let totalItems = 0;
  for (let di = 0; di < dayNotes.length; di++) {
    const dn = dayNotes[di];
    if (!isObj(dn)) return { ok: false, error: `dayNotes[${di}] 不是对象` };
    if (typeof dn.date !== 'string' || !DATE_RE.test(dn.date))
      return { ok: false, error: `dayNotes[${di}].date 必须为 YYYY-MM-DD` };
    if (!Array.isArray(dn.items))
      return { ok: false, error: `dayNotes[${di}].items 必须是数组` };
    if (dn.items.length > IMPORT_LIMITS.maxItemsPerDay)
      return { ok: false, error: `dayNotes[${di}] 条目数超过单日上限` };
    totalItems += dn.items.length;
    if (totalItems > IMPORT_LIMITS.maxTotalItems)
      return { ok: false, error: `条目总数超过上限 ${IMPORT_LIMITS.maxTotalItems}` };
    for (let ii = 0; ii < dn.items.length; ii++) {
      const err = validateItem(dn.items[ii], di, ii);
      if (err) return { ok: false, error: err };
    }
  }

  // 通过校验，归一化为强类型（设置字段宽松通过）
  const normalized: DayNoteExport = {
    app: 'daynote',
    version: EXPORT_VERSION,
    exportedAt: typeof migrated.exportedAt === 'string' ? migrated.exportedAt : new Date().toISOString(),
    source: migrated.source === 'cloud' ? 'cloud' : 'local',
    data: {
      groups: (groups as ExportGroup[]).map((g) => ({
        abbr: g.abbr,
        name: g.name,
        color: g.color ?? null,
        sortOrder: typeof g.sortOrder === 'number' ? g.sortOrder : 0,
      })),
      dayNotes: (dayNotes as ExportDayNote[]).map((dn) => ({
        date: dn.date,
        items: dn.items.map(
          (it: ExportItem, idx: number): ExportItem => ({
            content: it.content,
            note: it.note ?? null,
            color: it.color ?? null,
            hours: it.hours ?? null,
            isDone: it.isDone ?? false,
            sortOrder: typeof it.sortOrder === 'number' ? it.sortOrder : idx + 1,
            groupAbbr: it.groupAbbr ?? null,
          }),
        ),
      })),
      settings: isObj(data.settings) ? (data.settings as DayNoteExport['data']['settings']) : undefined,
    },
  };
  return { ok: true, data: normalized };
}
